import axios from "axios";
import React, { useEffect, useState } from "react";
import { Autocomplete, Box } from "@mui/material";
import "@/components/postcode-address/styles.css";
import { formatPostcodeValue } from "@/app/utils/helper";
import TextFieldStyled from "../detailed-form/components/TextFieldStyled";
import { useDebounce, useLocalStorage } from "../detailed-form/utils/hooks";
import { GET_API_BASE_URL } from "@/app/utils/functions";

const PostcodeAddressEquifax = ({
  id = "postcode-field",
  value,
  onAddressSelect,
  autoFocus = true,
  shrinkLabel = false,
  placeholder,
  hideLabel = false,
}) => {
  const [postcode, setPostcode] = useState(value?.postcode || null);
  const [errorMessage, setErrorMessage] = useState("");
  const [suggestedAddresses, setSuggestedAddresses] = useState(
    value?.addressList || null
  );
  const [state, setState] = useState(value?.addressList ? "success" : null);
  const [selectedAddress, setSelectedAddress] = useState(
    value?.address || null
  );
  const { addDebounce } = useDebounce();
  const { sessionId, loadFromStorage } = useLocalStorage();

  useEffect(() => {
    value?.postcode && setPostcode(value.postcode);
    value?.addressList && setSuggestedAddresses(value.addressList);
    value?.address && setSelectedAddress(value.address);
  }, [value]);
  /**
   * Handles changes to the postcode input field.
   * Formats the input value, clears suggested addresses, selected address, and state,
   * and then delays the lookup of addresses using debounce.
   *
   * @param {Event} e - The event object representing the change event.
   */
  const handlePostcodeChange = (e) => {
    // Get the value of the input field from the event object
    const { value = "" } = e.target;

    // Format the postcode value using the formatPostcodeValue function
    // and the current postcode state and the input value from the event target
    setPostcode(formatPostcodeValue(postcode, value));

    // Clear the suggested addresses, selected address, and state
    setSuggestedAddresses(null);
    setSelectedAddress(null);
    setState(null);

    // Delay the lookup of addresses using debounce
    addDebounce(() => {
      value && handleLookupAddress(value);
    });
  };

  const handleLookupAddress = async (postCode) => {
    setState("loading");
    try {
      const claim_number = loadFromStorage("currentId");
      const payload = { claim_number: claim_number, postal_code: postCode };
      const addressLookupResponse = await axios.post(
        `${GET_API_BASE_URL()}/credit-check/postal-to-address`,
        payload,
          { withCredentials: true }
      );
      const {
        listAddressByPostcodeResponse: { matchedStructuredAddress = [] } = {},
      } = addressLookupResponse?.data?.responseObject || {};
      const formattedSuggestions = matchedStructuredAddress.map((val) => {
        const {
          addressID,
          country,
          county,
          district,
          name,
          number,
          poBox,
          postcode,
          postTown,
          street1,
          street2,
          subBuilding,
        } = val || {};

        const address = [];
        if (poBox) address.push(poBox);
        if (subBuilding) address.push(subBuilding);
        if (name) address.push(name);
        if (number) address.push(number);
        if (street1) address.push(street1);
        if (street2) address.push(street2);
        if (district) address.push(district);
        if (postTown) address.push(postTown);
        if (postcode) address.push(postcode);
        if (county) address.push(county);
        if (country) address.push(country);

        const addressLabel = address.join(", ");
        return {
          label: addressLabel,
          value: { address: addressLabel, addressID: addressID, ...val },
        };
      });
      setSuggestedAddresses(formattedSuggestions);

      if (formattedSuggestions !== null && formattedSuggestions.length === 0)
        return setState("error");
      setState("success");
    } catch (error) {
      console.error("POSTAL CODE ERROR", { error });
      const { response: { data: { message } = {} } = {} } = error || {};
      message && setErrorMessage(message);
      setSuggestedAddresses([]);
      setState("error");
    }
  };

  const handleAddressSelect = (e, values) => {
    setSelectedAddress(values?.value?.address || "");
    onAddressSelect({
      postcode: postcode,
      addressList: suggestedAddresses,
      ...(values?.value || {}),
    });
    const nxtCTA = document.getElementById("next-cta-global");
    if (nxtCTA) {
      nxtCTA.scrollIntoView({
        block: "center",
      });
    }
  };

  const hasError =
    suggestedAddresses !== null && suggestedAddresses.length === 0;

  return (
    <Box
      component="fieldset"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "15px",
        borderColor: "transparent",
        minWidth: "100%",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "row", columnGap: "8px" }}>
        <TextFieldStyled
          id={id}
          fullWidth
          variant="outlined"
          placeholder={placeholder || ""}
          label={hideLabel ? null : "Postcode"}
          margin="normal"
          value={postcode}
          name="postcode"
          onChange={handlePostcodeChange}
          // InputLabelProps={{ shrink: false }}
          error={hasError}
          helperText={
            hasError && (errorMessage || "Please enter a valid postcode")
          }
          state={state}
          autoFocus={autoFocus}
          InputLabelProps={{
            shrink: shrinkLabel,
          }}
          showInputProgress={true}
        />
      </Box>
      {suggestedAddresses && suggestedAddresses.length ? (
        <Box>
          <Autocomplete
            options={suggestedAddresses}
            value={selectedAddress}
            onChange={handleAddressSelect}
            disableClearable={true}
            renderInput={(params) => (
              <TextFieldStyled
                {...params}
                variant="outlined"
                fullWidth
                placeholder={"Select Address"}
                label="Select your Address"
                hideClear={true}
              />
            )}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default PostcodeAddressEquifax;
